import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CheckCircle2, Compass, AlertCircle, FileSpreadsheet } from "lucide-react";
import Modal from "../../components/ui/Modal";
import { ApiService } from "../../services/api";
import type {
  DiagnosticStartResponse,
  DiagnosticSubmitResponse,
} from "../../types/navigator";

interface PracticeScope {
  sectionId?: string;
  unit?: string;
  count?: number;
  scopeLabel?: string;
}

interface Props {
  lessonId?: string;
  practice?: PracticeScope; // varsa serbest deneme, yoksa tanı
  onClose: () => void;
  onComplete: () => void;
}

const COUNT_OPTIONS = [10, 20, 40, 60];

const optionLabel = (opt: any): string => {
  if (opt == null) return "";
  if (typeof opt === "string" || typeof opt === "number") return String(opt);
  if (typeof opt === "object") return String(opt.label ?? opt.text ?? opt.value ?? JSON.stringify(opt));
  return String(opt);
};

export default function DiagnosticRunner({ lessonId, practice, onClose, onComplete }: Props) {
  const isPractice = !!practice;
  const needsSetup = isPractice && practice?.count == null;

  const [phase, setPhase] = useState<"setup" | "run">(needsSetup ? "setup" : "run");
  const [loading, setLoading] = useState(!needsSetup);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DiagnosticStartResponse | null>(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<DiagnosticSubmitResponse | null>(null);

  const startSession = useCallback(
    async (count?: number) => {
      try {
        setLoading(true);
        setError(null);
        setPhase("run");
        const res = isPractice
          ? await ApiService.startPractice({
              lessonId,
              sectionId: practice?.sectionId,
              unit: practice?.unit,
              count: count ?? practice?.count,
            })
          : await ApiService.startDiagnostic(lessonId);
        setData(res);
        setIdx(0);
        setAnswers({});
      } catch (e: any) {
        setError(e?.message || "Başlatılamadı");
      } finally {
        setLoading(false);
      }
    },
    [isPractice, lessonId, practice]
  );

  useEffect(() => {
    if (!needsSetup) startSession();
  }, []); // yalnızca mount'ta

  const questions = data?.questions || [];
  const q = questions[idx];
  const answeredCount = useMemo(() => Object.keys(answers).length, [answers]);
  const isLast = idx >= questions.length - 1;

  const choose = (value: any) => {
    if (!q) return;
    setAnswers((prev) => ({ ...prev, [q.id]: value }));
  };

  const handleSubmit = async () => {
    if (!data) return;
    try {
      setSubmitting(true);
      const payload = Object.entries(answers).map(([questionId, answer]) => ({ questionId, answer }));
      const res = await ApiService.submitDiagnostic(data.diagnosticId, payload);
      setResult(res);
    } catch (e: any) {
      setError(e?.message || "Gönderilemedi");
    } finally {
      setSubmitting(false);
    }
  };

  const scopeLabel = practice?.scopeLabel || data?.scopeLabel;
  const title = result
    ? isPractice
      ? "Deneme tamamlandı"
      : "Tanı tamamlandı"
    : phase === "setup"
    ? "Deneme oluştur"
    : isPractice
    ? `Deneme${scopeLabel ? ` · ${scopeLabel}` : ""}`
    : `Seviye Tespiti${data ? ` · ${data.lesson.title}` : ""}`;

  return (
    <Modal isOpen onClose={onClose} title={title} size="lg" closeOnBackdrop={false}>
      {/* Kurulum: soru sayısı seç */}
      {phase === "setup" && !result && (
        <div className="py-4 text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-lg bg-blue-50 text-blue-600">
            <FileSpreadsheet className="h-6 w-6" />
          </div>
          <p className="mb-1 text-base font-semibold text-gray-900">
            {scopeLabel ? `${scopeLabel} · deneme` : "Deneme oluştur"}
          </p>
          <p className="mx-auto mb-5 max-w-sm text-sm text-gray-600">Kaç soruluk bir deneme çözmek istersin?</p>
          <div className="mx-auto grid max-w-sm grid-cols-4 gap-2">
            {COUNT_OPTIONS.map((c) => (
              <button
                key={c}
                onClick={() => startSession(c)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-sm font-semibold text-gray-800 transition-colors hover:border-blue-400 hover:bg-blue-50"
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Yükleniyor */}
      {phase === "run" && loading && (
        <div className="grid place-items-center py-16">
          <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      )}

      {/* Hata */}
      {!loading && error && !result && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle className="h-5 w-5 shrink-0" />
          {error}
        </div>
      )}

      {/* Sonuç */}
      {result && (
        <div className="py-4 text-center">
          <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-emerald-100 text-emerald-600">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <p className="mb-1 text-lg font-semibold text-gray-900">Haritan güncellendi!</p>
          <p className="mx-auto mb-4 max-w-sm text-sm text-gray-600">
            {result.stats.answered} soru çözdün · {result.stats.correct} doğru, {result.stats.wrong} yanlış.
          </p>
          <div className="mb-5 flex items-center justify-center gap-2">
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-center">
              <p className="text-lg font-bold text-blue-600">%{result.stats.percent}</p>
              <p className="text-[11px] text-gray-400">başarı</p>
            </div>
            {result.deficienciesCreated > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-center">
                <p className="text-lg font-bold text-rose-600">{result.deficienciesCreated}</p>
                <p className="text-[11px] text-gray-400">eksik bulundu</p>
              </div>
            )}
          </div>
          <button
            onClick={() => {
              onComplete();
              onClose();
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            <Compass className="h-4 w-4" /> Haritamı gör
          </button>
        </div>
      )}

      {/* Soru akışı */}
      {phase === "run" && !loading && !error && !result && q && (
        <div>
          <div className="mb-4">
            <div className="mb-1.5 flex items-center justify-between text-xs text-gray-500">
              <span>
                Soru {idx + 1} / {questions.length}
              </span>
              <span>{answeredCount} cevaplandı</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-blue-600 transition-all"
                style={{ width: `${((idx + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="mb-3">
            <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-medium text-blue-700">
              {q.unit} · {q.konu}
            </span>
          </div>

          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
            {q.media?.url ? (
              <img src={q.media.url} alt="Soru" className="mx-auto max-h-[46vh] w-auto max-w-full rounded-md object-contain" />
            ) : q.content ? (
              <p className="whitespace-pre-wrap text-sm text-gray-800">{q.content}</p>
            ) : (
              <p className="text-sm text-gray-400">Soru içeriği görüntülenemedi.</p>
            )}
          </div>

          <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-5">
            {q.options.map((opt, i) => {
              const label = optionLabel(opt);
              const selected = JSON.stringify(answers[q.id]) === JSON.stringify(opt);
              return (
                <button
                  key={i}
                  onClick={() => choose(opt)}
                  className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${
                    selected
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-gray-50"
                  }`}
                >
                  {label || String.fromCharCode(65 + i)}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={idx === 0}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" /> Önceki
            </button>

            {!isLast ? (
              <button
                onClick={() => setIdx((i) => Math.min(questions.length - 1, i + 1))}
                className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                Sonraki <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting || answeredCount === 0}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
              >
                {submitting ? "Gönderiliyor…" : "Bitir"}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
